import { queryOptions } from '@tanstack/react-query'
import { apiExchange, apiGet, apiPost, ApiError } from './client'
import { saveBytes } from './inventoree'

/**
 * Settings -> Reports -> Customer Reports, calea noua. Perechile vechi, din
 * `application/controllers/settings/Company_reports.php`:
 *
 *   customer_report                -> GET  /settings/company-reports/customer
 *   getCustomerReportDetails       -> POST /settings/company-reports/customer/table
 *   customerReportSummary /
 *   ...WorkstationSupport /
 *   ...NetworkSupport /
 *   ...OngoingProjects             -> POST /settings/company-reports/customer/{company}/{section}
 *   export_customer_report         -> GET  /settings/company-reports/customer/{company}/export.pdf
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`/`apiPost`.
 *
 * SECTIUNILE VIN CA HTML: serverul randeaza chiar machetele portalului
 * (`customer_report/*.php`), ca raportul pe care il citeste clientul sa aiba
 * aceeasi forma in amandoua. Pagina il pune in `.details_content`, ca
 * `actionTabs-1.js`.
 */

export type CustomerReportSection = { id: string; label: string; type: number }

export type CustomerReportShell = {
  title: string
  moduleUrl: string
  today: string
  sections: CustomerReportSection[]
}

export type CustomerReportRow = {
  ID: number
  name: string
  devices: number
  servers: number
  incidents: number
  user_request: number
  proactive: number
  workstation_effort: string
  noc_effort: string
  DT_RowId: string
}

export type CustomerReportTable = {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: CustomerReportRow[]
  from: string
  to: string
}

export type CustomerReportTableRequest = {
  draw: number
  start: number
  length: number
  orderColumn: number
  orderDir: 'asc' | 'desc'
  search: string
  filterFrom: string
  filterTo: string
}

export const customerReportShellQuery = queryOptions({
  queryKey: ['settings', 'company-reports', 'customer', 'shell'],
  queryFn: () => apiGet<CustomerReportShell>('/settings/company-reports/customer'),
})

export function customerReportTableQuery(r: CustomerReportTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'company-reports', 'customer', 'table', r],
    queryFn: () =>
      apiPost<CustomerReportTable>('/settings/company-reports/customer/table', {
        draw: r.draw,
        start: r.start,
        length: r.length,
        'order[0][column]': r.orderColumn,
        'order[0][dir]': r.orderDir,
        'search[value]': r.search,
        filterFrom: r.filterFrom,
        filterTo: r.filterTo,
      }),
    enabled: r.filterFrom !== '' && r.filterTo !== '',
  })
}

export function customerReportSectionQuery(company: number, section: string, from: string, to: string) {
  return queryOptions({
    queryKey: ['settings', 'company-reports', 'customer', 'section', company, section, from, to],
    queryFn: () =>
      apiPost<{ html: string; from: string; to: string; company: number }>(
        `/settings/company-reports/customer/${company}/${section}`,
        { from, to },
      ),
    enabled: company > 0 && section !== '',
  })
}

/** "Export PDF" - `export_customer_report`: fisierul "Customer report.pdf". */
export async function downloadCustomerReportPdf(company: number, from: string, to: string): Promise<void> {
  const p = new URLSearchParams({ from, to })
  const out = await apiExchange(`/settings/company-reports/customer/${company}/export.pdf?${p.toString()}`, { method: 'GET' })
  if (!out.type.includes('pdf')) {
    let message = 'The customer report PDF could not be produced.'
    try {
      message = (JSON.parse(out.text) as { message?: string }).message ?? message
    } catch {
      /* Raspunsul nu era JSON; ramane propozitia de mai sus. */
    }
    throw new ApiError(out.status || 501, message)
  }
  saveBytes(out.bytes, out.type, out.filename, 'Customer report.pdf')
}
